import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import RegistryMap from "@/components/registry/RegistryMap";
import SensoryRadar from "@/components/registry/SensoryRadar";
import VelvetWall from "@/components/registry/VelvetWall";
import { ASSETS } from "@/lib/brand";

const API = `${window.location.origin}/api`;

const GOLD = "#C4A671";
const WINES = ["Tutti", "Blanche", "Scilla", "Terra Arsa"];

function DishDetails({ dish }) {
  return (
    <div className="flex flex-col items-center gap-1.5 text-center">
      <span className="overline text-[8.5px]" style={{ color: "#8B7A55" }}>
        Matrice delle Affinità Sensoriali
      </span>
      <SensoryRadar sensory={dish.sensory} size={200} />
      <div className="font-serif-display text-[26px]" style={{ color: GOLD }}>
        {Number(dish.sensory?.voto ?? 0).toFixed(1)}
        <span className="text-[13px]" style={{ color: "#7A7568" }}> / 10</span>
      </div>
      <p className="text-[11.5px] leading-relaxed font-light px-4" style={{ color: "#C9C3B5" }}>
        {(dish.ingredients || []).join(" · ")}
      </p>
      <span className="overline text-[9px] mt-1" style={{ color: GOLD }}>
        {dish.pairing?.wine} · {dish.pairing?.lot}
      </span>
    </div>
  );
}

function DishCard({ dish, active, expanded, onFly, onToggle }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      id={`piatto-${dish.id}`}
      data-testid={`dish-card-${dish.id}`}
      onClick={() => onFly(dish.id)}
      className={`group border transition-colors duration-500 lg:cursor-pointer ${
        active ? "border-[#C4A671]/70" : "border-[#1c1c1c] hover:border-[#3a3a3a]"
      }`}
      style={{ background: "#0D0D0D" }}
    >
      <div className="relative overflow-hidden">
        <img
          src={dish.photo_url}
          alt={dish.title}
          loading="lazy"
          className="w-full aspect-[4/3] object-cover transition-transform duration-[1.2s] group-hover:scale-[1.045]"
          style={{ background: "#141414" }}
        />
        <div
          className="hidden lg:flex absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex-col items-center justify-center"
          style={{ background: "rgba(8,8,8,0.82)", backdropFilter: "blur(7px)" }}
        >
          <DishDetails dish={dish} />
        </div>
      </div>
      <div className="px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-serif-display text-[20px] leading-snug text-[#F2EFE8]">
              {dish.title}
            </h3>
            <p className="overline text-[9px] mt-2" style={{ color: "#8B7A55" }}>
              {dish.restaurant_display || "Maison Riservata"} · {dish.city}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span
              className="overline text-[8.5px] px-2.5 py-1 border whitespace-nowrap"
              style={{ borderColor: "rgba(196,166,113,0.4)", color: GOLD }}
            >
              {dish.pairing?.wine}
            </span>
            <button
              data-testid={`dish-toggle-${dish.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(dish.id);
              }}
              className="lg:hidden p-1 text-[#7A7568]"
              aria-label="Dettagli piatto"
            >
              <ChevronDown
                size={18}
                className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="pt-5 mt-4 border-t border-[#1c1c1c]">
                <DishDetails dish={dish} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
}

function Counter({ stats }) {
  if (!stats) return null;
  return (
    <div className="flex items-baseline gap-3" data-testid="registry-counter">
      <span className="font-serif-display text-[38px] leading-none" style={{ color: GOLD }}>
        {stats.paesi}
      </span>
      <span className="text-[12.5px] font-light leading-snug max-w-[280px]" style={{ color: "#C9C3B5" }}>
        Paesi — Registro consultato da professionisti e buyer d&apos;élite
      </span>
    </div>
  );
}

function Filters({ wine, setWine, city, setCity, cities }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="registry-filters">
      {WINES.map((w) => (
        <button
          key={w}
          data-testid={`filter-wine-${w.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => setWine(w)}
          className="px-4 py-2 text-[9.5px] uppercase tracking-[0.22em] border transition-colors duration-300"
          style={{
            borderColor: wine === w ? GOLD : "#2a2a2a",
            color: wine === w ? GOLD : "#7a7a7a",
            background: wine === w ? "rgba(196,166,113,0.07)" : "transparent",
          }}
        >
          {w}
        </button>
      ))}
      <select
        data-testid="filter-city"
        value={city}
        onChange={(e) => setCity(e.target.value)}
        className="ml-auto bg-[#111] border border-[#2a2a2a] px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-[#C9C3B5] outline-none focus:border-[#C4A671]"
      >
        <option value="Tutte">Tutte le città</option>
        {cities.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    </div>
  );
}

export default function Registry() {
  const [dishes, setDishes] = useState([]);
  const [stats, setStats] = useState(null);
  const [wine, setWine] = useState("Tutti");
  const [city, setCity] = useState("Tutte");
  const [activeId, setActiveId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [wallOpen, setWallOpen] = useState(false);
  const [params] = useSearchParams();
  const deepDone = useRef(false);
  const codeParam = params.get("codice") || "";

  useEffect(() => {
    if (codeParam) setWallOpen(true);
  }, [codeParam]);

  useEffect(() => {
    document.title = "Allocazioni d'Autore — La Valle degli Iblei";
    axios.get(`${API}/registry/dishes`).then(({ data }) => setDishes(data.dishes || [])).catch(() => {});
    axios.get(`${API}/registry/stats`).then(({ data }) => setStats(data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (deepDone.current || !dishes.length) return;
    deepDone.current = true;
    const pid = params.get("piatto");
    if (!pid || !dishes.some((d) => d.id === pid)) return;
    setActiveId(pid);
    setExpandedId(pid);
    setTimeout(() => {
      document.getElementById(`piatto-${pid}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 500);
  }, [dishes, params]);

  const cities = [...new Set(dishes.map((d) => d.city))].sort();
  const filtered = dishes.filter(
    (d) => (wine === "Tutti" || d.pairing?.wine === wine) && (city === "Tutte" || d.city === city)
  );

  const flyFromMap = (id) => {
    setActiveId(id);
    document.getElementById(`piatto-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-[#F2EFE8]" data-testid="registry-page">
      <div className="fixed inset-0 grain opacity-20 pointer-events-none" aria-hidden />

      {/* Header */}
      <header
        className="sticky top-0 z-40 border-b border-[#1a1a1a]"
        style={{ background: "rgba(10,10,10,0.88)", backdropFilter: "blur(12px)" }}
        data-testid="registry-header"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 flex items-center justify-between h-[68px]">
          <Link to="/" data-testid="registry-home-link" className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="La Valle degli Iblei" className="h-9 w-9 object-contain" />
            <span className="flex flex-col leading-tight text-left">
              <span className="font-serif-display text-[14px] text-[#F2EFE8]">La Valle degli Iblei</span>
              <span className="overline text-[8.5px]" style={{ color: "#8B7A55" }}>
                Registro Globale delle Allocazioni
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="registry-back-link"
              className="hidden md:block overline text-[9.5px] text-[#7A7568] hover:text-[#C9C3B5] transition-colors"
            >
              ← Torna alla Maison
            </Link>
            <button
              data-testid="registra-piatto-btn"
              onClick={() => setWallOpen(true)}
              className="hidden lg:block px-6 py-2.5 text-[10px] tracking-[0.24em] uppercase transition-opacity hover:opacity-90"
              style={{ background: GOLD, color: "#0A0A0A" }}
            >
              Registra Piatto
            </button>
          </div>
        </div>
      </header>

      <div className="relative lg:grid lg:grid-cols-[45%_55%] max-w-[1600px] mx-auto">
        {/* Left — sticky map (desktop) */}
        <aside className="hidden lg:block border-r border-[#1a1a1a]">
          <div className="sticky top-[68px] h-[calc(100vh-68px)] flex flex-col justify-center px-10 xl:px-14 py-8">
            <div className="flex items-center gap-5 mb-5">
              <div className="h-px w-10" style={{ background: GOLD }} />
              <span className="overline text-[9.5px]" style={{ color: GOLD }}>
                Il Registro Globale
              </span>
            </div>
            <h1
              className="font-serif-display leading-[0.95] text-[#F2EFE8]"
              style={{ fontSize: "clamp(2.2rem, 3.2vw, 3.4rem)" }}
            >
              Allocazioni{" "}
              <span className="italic font-light" style={{ color: GOLD }}>d&apos;Autore.</span>
            </h1>
            <p className="mt-4 mb-7 text-[14px] leading-[1.85] font-light max-w-md" style={{ color: "#C9C3B5" }}>
              Le creazioni delle grandi cucine nate attorno alle nostre allocazioni.
              Ogni piatto è iscritto al Registro con la propria Matrice delle
              Affinità Sensoriali.
            </p>
            <div className="mb-7">
              <Counter stats={stats} />
            </div>
            <RegistryMap dishes={filtered} activeId={activeId} onSelect={flyFromMap} />
            <p className="mt-4 overline text-[8.5px]" style={{ color: "#7a7568" }}>
              Ogni punto è una creazione iscritta · Le identità restano riservate, salvo consenso
            </p>
          </div>
        </aside>

        {/* Right — feed */}
        <section className="px-6 sm:px-10 xl:px-14 py-10 lg:py-12">
          {/* Mobile intro */}
          <div className="lg:hidden mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="h-px w-8" style={{ background: GOLD }} />
              <span className="overline text-[9px]" style={{ color: GOLD }}>Il Registro Globale</span>
            </div>
            <h1 className="font-serif-display text-4xl leading-[0.98] text-[#F2EFE8]">
              Allocazioni <span className="italic font-light" style={{ color: GOLD }}>d&apos;Autore.</span>
            </h1>
            <p className="mt-4 text-sm leading-[1.85] font-light" style={{ color: "#C9C3B5" }}>
              Le creazioni delle grandi cucine nate attorno alle nostre allocazioni,
              iscritte al Registro con la propria Matrice Sensoriale.
            </p>
            <div className="mt-6 mb-6">
              <Counter stats={stats} />
            </div>
            <RegistryMap dishes={filtered} activeId={null} interactive={false} />
          </div>

          <div className="mb-8">
            <Filters wine={wine} setWine={setWine} city={city} setCity={setCity} cities={cities} />
          </div>

          <div className="flex flex-col gap-8" data-testid="registry-feed">
            {filtered.map((d) => (
              <DishCard
                key={d.id}
                dish={d}
                active={activeId === d.id}
                expanded={expandedId === d.id}
                onFly={(id) => setActiveId(id)}
                onToggle={(id) => setExpandedId((p) => (p === id ? null : id))}
              />
            ))}
            {filtered.length === 0 && (
              <p className="text-center py-16 text-[13px] font-light" style={{ color: "#7A7568" }}>
                Nessuna creazione corrisponde ai filtri selezionati.
              </p>
            )}
          </div>

          <footer className="mt-16 pt-8 border-t border-[#1a1a1a] flex flex-wrap items-center justify-between gap-4">
            <span className="overline text-[8.5px]" style={{ color: "#7a7568" }}>
              © La Valle degli Iblei · Registro Globale delle Allocazioni
            </span>
            <Link to="/" className="overline text-[8.5px] hover:text-[#C9C3B5] transition-colors" style={{ color: "#8B7A55" }}>
              lavalledegliiblei.it
            </Link>
          </footer>
        </section>
      </div>

      {/* Mobile floating CTA */}
      <button
        data-testid="registra-piatto-mobile-btn"
        onClick={() => setWallOpen(true)}
        className="lg:hidden fixed bottom-5 right-5 z-40 px-6 py-3.5 text-[9.5px] tracking-[0.24em] uppercase shadow-2xl"
        style={{ background: GOLD, color: "#0A0A0A" }}
      >
        Registra Piatto
      </button>

      <VelvetWall open={wallOpen} onClose={() => setWallOpen(false)} initialCode={codeParam} />
    </main>
  );
}
